import cx from 'clsx';
import React from 'react';

import { ReactComponent as TableSort } from '@assets/svg/table-sort.svg';

import Button from '@components/uikit/Button/Button';
import Checkbox from '@components/uikit/Checkbox/Checkbox';

import { MODAL_NAMES } from '@constants/modalRenderers';

import useTranslation from '@hooks/useTranslation';

import type { ILocationStructureNode } from '@model/structureData';

import { useModalActions } from '@store/useModalStore';

import {
  selectAllFacilitiesChecked,
  selectColumnSubscriptions,
  selectEvents,
  selectSubscrLocations,
  selectSubscriptions,
  useSubscriptionsUserActions,
} from '../../store/useSubscriptionsUserStore';
import LocationSelectBlock from '../LocationSelectBlock/LocationSelectBlock';
import EmailNotificationsSkeleton from './EmailNotificationsUserSkeleton';
import style from './style.module.scss';

interface IProps {
  locations?: ILocationStructureNode[];
  isLoading?: boolean;
}

const EmailNotificationsUser: React.FC<IProps> = ({ locations, isLoading }) => {
  const { t } = useTranslation();

  const { setSecondaryModal, setSecondaryModalClose } = useModalActions();

  const userLocations: Array<number> = selectSubscrLocations();
  const columnSubscriptions = selectColumnSubscriptions();
  const subscriptions = selectSubscriptions();
  const allFacilitiesChecked = selectAllFacilitiesChecked();
  const events: Array<{ event_id: number; name: string }> = selectEvents();
  const { setColumnSubscriptions, setAllFacilitiesChecked, setSubscriptions } = useSubscriptionsUserActions();

  const handleChangeSelectAll = () => {
    if (!allFacilitiesChecked) {
      setSecondaryModal({
        name: MODAL_NAMES.modalConfirm,
        options: {
          bodyTitle: t('Select All Facilities'),
          bodyContent: (
            <>
              <div className={style.mainBodyText}>{t('Are you sure you want to select All Facilities?')}</div>
              <div>
                {t(
                  'You’ve customized notifications for individual facilities. Selecting All Facilities will discard your custom settings',
                )}
              </div>
            </>
          ),
          confirmText: t('Select All Facilities'),
          onConfirm: () => {
            setAllFacilitiesChecked(1, locations?.map((loc) => loc.id));
          },
          onCancel: () => {
            setSecondaryModalClose();
          },
        },
      });
    } else {
      setAllFacilitiesChecked(0);
    }
  };

  if (isLoading) {
    return <EmailNotificationsSkeleton />;
  }

  return (
    <div className={style.settingsBlock}>
      <div className={style.facilityBlock}>
        <div className={cx(style.blockHead, style.facilityBlockHead)}>
          <span className={style.blockTitle}>
            {t('Facility')}
            <TableSort className={style.sortIcon} />
          </span>
          {locations && locations.length > 1 && (
            <Button
              type="secondary"
              text={!allFacilitiesChecked ? t('Select all') : t('Unselect all')}
              onClick={handleChangeSelectAll}
            />
          )}
        </div>
        <div className={style.facilityBlockBody}>
          <LocationSelectBlock locations={locations} />
        </div>
      </div>
      {!userLocations.length ? (
        <div className={style.emptyBlockWrapper}>
          <div className={style.emptyBlockText}>{t('No facilities have been selected')}</div>
        </div>
      ) : (
        <div>
          <div className={cx(style.blockHead, style.notificationsBlockHead)}>
            <span className={style.blockTitle}>{t('Actions')}</span>
            <Checkbox
              value={!!columnSubscriptions?.[1]}
              onChange={({ target }) =>
                setColumnSubscriptions({
                  checked: target.checked,
                  urgencyKey: 1,
                })
              }
              className={style.checkboxStandard}
              label={t('Standard')}
              disabled={isLoading}
              withHelperText={false}
            />
            <Checkbox
              value={!!columnSubscriptions?.[2]}
              onChange={({ target }) =>
                setColumnSubscriptions({
                  checked: target.checked,
                  urgencyKey: 2,
                })
              }
              className={style.checkboxHigh}
              label={t('High')}
              disabled={isLoading}
              withHelperText={false}
            />
            <Checkbox
              value={!!columnSubscriptions?.[3]}
              onChange={({ target }) =>
                setColumnSubscriptions({
                  checked: target.checked,
                  urgencyKey: 3,
                })
              }
              className={style.checkboxCritical}
              label={t('Critical')}
              disabled={isLoading}
              withHelperText={false}
            />
          </div>
          <div className={style.notificationsBlockBody}>
            {events.map((event) => (
              <div key={event.event_id} className={style.notificationsRow}>
                <span className={style.notificationsRowName}>{t(event.name)}</span>
                <Checkbox
                  value={subscriptions?.[event.event_id]?.[userLocations?.[0]]?.urgencies[1]}
                  onChange={({ target }) =>
                    setSubscriptions({ checked: +target.checked, eventId: event.event_id, urgencyKey: 1 })
                  }
                  className={style.checkboxStandard}
                  withHelperText={false}
                />
                <Checkbox
                  value={subscriptions?.[event.event_id]?.[userLocations?.[0]]?.urgencies[2]}
                  onChange={({ target }) =>
                    setSubscriptions({ checked: +target.checked, eventId: event.event_id, urgencyKey: 2 })
                  }
                  className={style.checkboxHigh}
                  withHelperText={false}
                />
                <Checkbox
                  value={subscriptions?.[event.event_id]?.[userLocations?.[0]]?.urgencies[3]}
                  onChange={({ target }) =>
                    setSubscriptions({ checked: +target.checked, eventId: event.event_id, urgencyKey: 3 })
                  }
                  className={style.checkboxCritical}
                  withHelperText={false}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailNotificationsUser;
